/**
 * Extract latitude/longitude from a Google Maps URL or coordinate string.
 *
 * Supported formats:
 *   https://www.google.com/maps/place/X/@36.7666,31.3893,15z
 *   https://www.google.com/maps/@36.7666,31.3893,15z
 *   https://maps.google.com/?q=36.7666,31.3893
 *   https://www.google.com/maps?q=36.7666,31.3893
 *   https://www.google.com/maps/place/...!3d36.7666!4d31.3893!...
 *   36.7666, 31.3893     (plain "lat,lng")
 *
 * Returns { lat, lng } or null if not parseable.
 * Note: Shortened URLs (goo.gl/maps, maps.app.goo.gl) cannot be parsed
 * client-side — those require server-side HTTP redirect resolution.
 */
export function parseGoogleMapsUrl(input) {
    if (!input || typeof input !== "string") return null;
    const s = input.trim();

    // 1) Plain "lat, lng" coordinates
    const plain = s.match(/^(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/);
    if (plain) {
        const lat = parseFloat(plain[1]);
        const lng = parseFloat(plain[2]);
        if (isFinite(lat) && isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
            return { lat, lng };
        }
    }

    // 2) @lat,lng,zoom format (e.g. ...@36.7666,31.3893,15z)
    const at = s.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)(?:,|$)/);
    if (at) {
        const lat = parseFloat(at[1]);
        const lng = parseFloat(at[2]);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }

    // 3) ?q=lat,lng or &q=lat,lng
    const q = s.match(/[?&]q=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
    if (q) {
        const lat = parseFloat(q[1]);
        const lng = parseFloat(q[2]);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }

    // 4) !3d{lat}!4d{lng} (Google Maps internal "data" param)
    const d3d4 = s.match(/!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/);
    if (d3d4) {
        const lat = parseFloat(d3d4[1]);
        const lng = parseFloat(d3d4[2]);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }

    // 5) ll=lat,lng (legacy)
    const ll = s.match(/[?&]ll=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
    if (ll) {
        const lat = parseFloat(ll[1]);
        const lng = parseFloat(ll[2]);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }

    return null;
}

/** Detect if input is a shortened URL that needs server resolution. */
export function isShortMapsUrl(input) {
    if (!input) return false;
    return /maps\.app\.goo\.gl|goo\.gl\/maps/i.test(input.trim());
}
